import React, { useEffect, useMemo, useState } from 'react';
import { BOAPI } from '../service/UsersService';

// הודעות באפליקציה + "הצעת החלטה": ההודעה מוצגת למשתמש בפתיחת האפליקציה (חלון עם כפתורים).
// אם נבחרה החלטה — "כן" מוסיף אותה להחלטות של המשתמש שלחץ (spAcknowledgeMessage, DecisionOffers.sql).
// בלי החלטה — הודעה רגילה עם כפתור "הבנתי".

const pad = (n) => String(n).padStart(2, '0');
const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };

const emptyForm = () => ({
  title: '',
  content: '',
  decisionId: '',
  targetAll: false,
  startDate: ymd(new Date()),
  endDate: ymd(addDays(new Date(), 7)),
  acceptText: '',
  declineText: '',
  sendPush: true,
});

const SystemMessagesTab = () => {
  const [form, setForm] = useState(emptyForm);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [users, setUsers] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [decisions, setDecisions] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState('');

  const load = async () => {
    try {
      setLoading(true);
      const resp = await BOAPI.fetchSystemMessages();
      if (resp?.[0]?.[0]?.code === 0) setMessages(resp[1] || []);
      else setError(resp?.[0]?.[0]?.msg || 'שגיאה בטעינת ההודעות');
    } catch (err) {
      setError(err.message || 'שגיאה בטעינת ההודעות');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    BOAPI.fetchDecisionOfferCatalog()
      .then((resp) => { if (resp?.[0]?.[0]?.code === 0) setDecisions(resp[1] || []); })
      .catch(() => setError('שגיאה בטעינת רשימת ההחלטות'));
    BOAPI.fetchUsers()
      .then((resp) => { if (resp?.[0]?.[0]?.code === 0) setUsers((resp[1] || []).filter((u) => u.isActive === 1)); })
      .catch(() => setError('שגיאה בטעינת רשימת המשתמשים'));
  }, []);

  const filteredUsers = useMemo(() => {
    const q = userSearch.trim();
    if (!q) return [];
    const chosen = new Set(selectedUsers.map((u) => u.id));
    return users
      .filter((u) => !chosen.has(u.id))
      .filter((u) => `${u.fName || ''} ${u.lName || ''}`.includes(q) || (u.username || '').includes(q) || String(u.id) === q)
      .slice(0, 20);
  }, [users, userSearch, selectedUsers]);

  const onChange = (field, value) => setForm((f) => ({ ...f, [field]: value }));
  const selectedDecision = decisions.find((d) => String(d.id) === String(form.decisionId));

  const validate = () => {
    if (!form.title.trim() || !form.content.trim()) return 'נא להזין כותרת ותוכן';
    if (!form.targetAll && selectedUsers.length === 0) return 'נא לבחור לפחות משתמש אחד, או "כל המשתמשים"';
    if (form.endDate < form.startDate) return 'תאריך הסיום לפני תאריך ההתחלה';
    return null;
  };

  const handleSend = async () => {
    const err = validate();
    if (err) { setError(err); return; }
    const audience = form.targetAll ? 'כל המשתמשים' : `${selectedUsers.length} משתמשים`;
    const what = selectedDecision ? `הצעת ההחלטה "${selectedDecision.name}"` : 'ההודעה';
    if (!window.confirm(`לשלוח את ${what} ל${audience}?${form.sendPush ? '\n(כולל התראת פוש)' : ''}`)) return;

    setError(null);
    setSuccessMsg('');
    try {
      setSending(true);
      const resp = await BOAPI.addSystemMessage({
        title: form.title.trim(),
        content: form.content.trim(),
        decisionId: form.decisionId || null,
        targetAll: form.targetAll ? '1' : '0',
        userIds: form.targetAll ? [] : selectedUsers.map((u) => u.id),
        startDate: form.startDate,
        endDate: form.endDate,
        acceptText: form.acceptText.trim(),
        declineText: form.declineText.trim(),
        sendPush: form.sendPush ? '1' : '0',
      });
      const result = resp?.[0]?.[0];
      if (result?.code === 0) {
        setSuccessMsg(`נשלח ל-${result.recipientCount} משתמשים. ההודעה תופיע להם בפתיחת האפליקציה.`);
        setForm(emptyForm());
        setSelectedUsers([]);
        load();
      } else {
        setError(result?.msg || 'שגיאה בשליחה');
      }
    } catch (e) {
      setError(e.message || 'שגיאה בשליחה');
    } finally {
      setSending(false);
    }
  };

  const toggleActive = async (m) => {
    const next = !m.isActive;
    if (!next && !window.confirm('להפסיק להציג את ההודעה למי שעוד לא ענה?')) return;
    try {
      const resp = await BOAPI.setSystemMessageActive(m.id, next);
      if (resp?.[0]?.[0]?.code === 0) load();
      else setError(resp?.[0]?.[0]?.msg || 'שגיאה בעדכון');
    } catch (e) {
      setError(e.message || 'שגיאה בעדכון');
    }
  };

  return (
    <div style={s.container}>
      <div style={s.headerSection}>
        <h2 style={s.title}>הודעות והצעת החלטה</h2>
      </div>

      {error && (
        <div style={s.error}>
          <span>{error}</span>
          <button onClick={() => setError(null)} style={s.closeErrorButton}>✕</button>
        </div>
      )}
      {successMsg && <div style={s.success}>{successMsg}</div>}

      <div style={s.formCard}>
        <div style={s.formGroup}>
          <label style={s.label}>כותרת *</label>
          <input type="text" value={form.title} onChange={(e) => onChange('title', e.target.value)} style={s.input} maxLength={128}
            placeholder="לדוגמה: החלטה חדשה בשבילך" />
        </div>
        <div style={s.formGroup}>
          <label style={s.label}>תוכן ההודעה *</label>
          <textarea value={form.content} onChange={(e) => onChange('content', e.target.value)} style={s.textarea} rows={3} maxLength={1000}
            placeholder="לדוגמה: האם תרצה להוסיף להחלטות שלך וידאו קצר של הרבי בכל יום?" />
        </div>

        <div style={s.formGroup}>
          <label style={s.label}>החלטה שתתווסף למי שמקבל</label>
          <select value={form.decisionId} onChange={(e) => onChange('decisionId', e.target.value)} style={s.input}>
            <option value="">— בלי החלטה (הודעה רגילה עם כפתור "הבנתי") —</option>
            {decisions.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
          {selectedDecision && (
            <div style={s.hint}>בלחיצה על "כן" ההחלטה "{selectedDecision.name}" תתווסף למשתמש כהחלטה יומית, מהיום והלאה.</div>
          )}
        </div>

        {selectedDecision && (
          <div style={s.formRow}>
            <div style={s.formGroup}>
              <label style={s.label}>טקסט כפתור קבלה</label>
              <input type="text" value={form.acceptText} onChange={(e) => onChange('acceptText', e.target.value)} style={s.input} maxLength={32}
                placeholder="כן, להוסיף להחלטות שלי" />
            </div>
            <div style={s.formGroup}>
              <label style={s.label}>טקסט כפתור דחייה</label>
              <input type="text" value={form.declineText} onChange={(e) => onChange('declineText', e.target.value)} style={s.input} maxLength={32}
                placeholder="לא תודה" />
            </div>
          </div>
        )}

        <div style={s.formRow}>
          <div style={s.formGroup}>
            <label style={s.label}>מוצג מתאריך</label>
            <input type="date" value={form.startDate} onChange={(e) => onChange('startDate', e.target.value)} style={s.input} />
          </div>
          <div style={s.formGroup}>
            <label style={s.label}>עד תאריך</label>
            <input type="date" value={form.endDate} onChange={(e) => onChange('endDate', e.target.value)} style={s.input} />
          </div>
        </div>

        <div style={s.targetSection}>
          <label style={s.label}>למי לשלוח</label>
          <div style={s.modeToggle}>
            <button style={{ ...s.modeBtn, ...(!form.targetAll ? s.modeBtnActive : {}) }} onClick={() => onChange('targetAll', false)}>משתמשים מסוימים</button>
            <button style={{ ...s.modeBtn, ...(form.targetAll ? s.modeBtnActive : {}) }} onClick={() => onChange('targetAll', true)}>כל המשתמשים</button>
          </div>

          {!form.targetAll && (
            <div style={s.formGroup}>
              {selectedUsers.length > 0 && (
                <div style={s.chips}>
                  {selectedUsers.map((u) => (
                    <span key={u.id} style={s.chip}>
                      {u.fName} {u.lName}
                      <button style={s.chipX} onClick={() => setSelectedUsers((list) => list.filter((x) => x.id !== u.id))}>✕</button>
                    </span>
                  ))}
                </div>
              )}
              <input
                type="text"
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
                style={s.input}
                placeholder="חיפוש משתמש לפי שם / שם משתמש / מזהה..."
              />
              {filteredUsers.length > 0 && (
                <div style={s.userResults}>
                  {filteredUsers.map((u) => (
                    <div key={u.id} style={s.userResultRow}
                      onClick={() => { setSelectedUsers((list) => [...list, u]); setUserSearch(''); }}>
                      {u.fName} {u.lName} <span style={s.userResultSub}>({u.username} · {u.id})</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <label style={s.checkboxRow}>
          <input type="checkbox" checked={form.sendPush} onChange={(e) => onChange('sendPush', e.target.checked)} />
          לשלוח גם התראת פוש (עם הכותרת והתוכן) — רק אם ההודעה מוצגת כבר מהיום
        </label>

        <div style={s.formButtons}>
          <button style={s.sendButton} onClick={handleSend} disabled={sending}>
            {sending ? 'שולח...' : '📤 שלח'}
          </button>
        </div>
      </div>

      <h3 style={s.historyTitle}>הודעות שנשלחו</h3>
      {loading ? (
        <div style={s.center}>טוען...</div>
      ) : messages.length === 0 ? (
        <div style={s.noData}>עדיין לא נשלחו הודעות</div>
      ) : (
        <div style={s.tableWrapper}>
          <table style={s.table}>
            <thead>
              <tr style={s.headerRow}>
                <th style={s.th}>הודעה</th>
                <th style={s.th}>החלטה</th>
                <th style={s.th}>נמענים</th>
                <th style={s.th}>תוקף</th>
                <th style={s.th}>קיבלו</th>
                <th style={s.th}>דחו</th>
                <th style={s.th}>טרם ענו</th>
                <th style={s.th}></th>
              </tr>
            </thead>
            <tbody>
              {messages.map((m) => {
                const pending = Math.max(0, (m.recipientCount || 0) - (m.acceptedCount || 0) - (m.declinedCount || 0));
                return (
                  <tr key={m.id} style={{ ...s.row, ...(m.isActive ? {} : s.rowInactive) }}>
                    <td style={s.td}>
                      <div style={s.msgTitle}>{m.title}</div>
                      <div style={s.msgContent}>{m.content}</div>
                    </td>
                    <td style={s.td}>{m.decisionName || '—'}</td>
                    <td style={s.td} title={m.recipientNames || ''}>
                      {m.targetAll ? `כולם (${m.recipientCount})` : (m.recipientNames || m.recipientCount)}
                    </td>
                    <td style={s.td}>{m.startDate} – {m.endDate}</td>
                    <td style={{ ...s.td, ...s.num, color: '#2e7d32' }}>{m.acceptedCount}</td>
                    <td style={{ ...s.td, ...s.num, color: '#c62828' }}>{m.declinedCount}</td>
                    <td style={{ ...s.td, ...s.num }}>{pending}</td>
                    <td style={s.td}>
                      <button style={m.isActive ? s.stopButton : s.resumeButton} onClick={() => toggleActive(m)}>
                        {m.isActive ? 'הפסק' : 'הפעל'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const s = {
  container: { padding: '20px', direction: 'rtl', fontFamily: 'Arial, sans-serif' },
  headerSection: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px', marginBottom: '20px' },
  title: { margin: 0, color: '#1a1f36', fontSize: '24px', fontWeight: 'bold' },

  formCard: { backgroundColor: '#f9f9f9', padding: '20px', borderRadius: '8px', border: '1px solid #ddd', marginBottom: '30px' },
  formGroup: { marginBottom: '15px', position: 'relative' },
  formRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '15px' },
  label: { display: 'block', marginBottom: '5px', color: '#333', fontWeight: 'bold', fontSize: '14px' },
  input: { width: '100%', padding: '10px', fontSize: '14px', border: '1px solid #ddd', borderRadius: '4px', direction: 'rtl', textAlign: 'right', boxSizing: 'border-box' },
  textarea: { width: '100%', padding: '10px', fontFamily: 'Arial, sans-serif', fontSize: '14px', border: '1px solid #ddd', borderRadius: '4px', direction: 'rtl', boxSizing: 'border-box', resize: 'vertical' },
  hint: { marginTop: '6px', fontSize: '13px', color: '#697386' },

  modeToggle: { display: 'flex', gap: '10px', marginBottom: '15px' },
  modeBtn: { padding: '8px 16px', backgroundColor: '#fff', color: '#697386', border: '1px solid #ddd', borderRadius: '20px', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold' },
  modeBtnActive: { backgroundColor: '#1f6feb', color: '#fff', borderColor: '#1f6feb' },

  targetSection: { marginTop: '10px', paddingTop: '15px', borderTop: '1px dashed #ddd' },
  chips: { display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '8px' },
  chip: { display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '4px 10px', backgroundColor: '#e8f0fe', color: '#1f6feb', borderRadius: '14px', fontSize: '13px', fontWeight: 'bold' },
  chipX: { background: 'transparent', border: 'none', color: '#1f6feb', cursor: 'pointer', fontSize: '12px', padding: 0 },
  userResults: { position: 'absolute', zIndex: 5, top: '100%', left: 0, right: 0, backgroundColor: '#fff', border: '1px solid #ddd', borderRadius: '4px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', maxHeight: '220px', overflowY: 'auto' },
  userResultRow: { padding: '10px 12px', cursor: 'pointer', fontSize: '14px', borderBottom: '1px solid #f0f0f0' },
  userResultSub: { color: '#999', fontSize: '12px' },

  checkboxRow: { display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: '#333', margin: '6px 0 4px', cursor: 'pointer' },
  formButtons: { display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '10px' },
  sendButton: { padding: '10px 24px', backgroundColor: '#2e7d32', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold' },

  historyTitle: { color: '#1a1f36', fontSize: '18px', fontWeight: 'bold', marginBottom: '15px' },
  tableWrapper: { overflowX: 'auto', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' },
  table: { width: '100%', borderCollapse: 'collapse', backgroundColor: 'white' },
  headerRow: { backgroundColor: '#f5f5f5', borderBottom: '2px solid #ddd' },
  th: { padding: '12px', textAlign: 'right', fontWeight: 'bold', color: '#333', fontSize: '14px', borderBottom: '2px solid #ddd', whiteSpace: 'nowrap' },
  row: { borderBottom: '1px solid #eee' },
  rowInactive: { opacity: 0.5 },
  td: { padding: '12px', color: '#666', fontSize: '14px', textAlign: 'right', verticalAlign: 'top' },
  num: { fontWeight: 'bold', textAlign: 'center' },
  msgTitle: { fontWeight: 'bold', color: '#1a1f36', marginBottom: '4px' },
  msgContent: { fontSize: '13px', color: '#697386', maxWidth: '320px' },
  stopButton: { padding: '6px 12px', backgroundColor: '#fff', color: '#c62828', border: '1px solid #c62828', borderRadius: '4px', cursor: 'pointer', fontSize: '13px' },
  resumeButton: { padding: '6px 12px', backgroundColor: '#fff', color: '#2e7d32', border: '1px solid #2e7d32', borderRadius: '4px', cursor: 'pointer', fontSize: '13px' },

  center: { textAlign: 'center', padding: '40px', color: '#999' },
  error: { padding: '15px', backgroundColor: '#ffebee', color: '#c62828', borderRadius: '4px', marginBottom: '20px', fontSize: '14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  closeErrorButton: { backgroundColor: 'transparent', color: '#c62828', border: 'none', cursor: 'pointer', fontSize: '18px', fontWeight: 'bold' },
  success: { padding: '15px', backgroundColor: '#e8f5e9', color: '#2e7d32', borderRadius: '4px', marginBottom: '20px', fontSize: '14px' },
  noData: { padding: '40px', textAlign: 'center', color: '#999', backgroundColor: '#f5f5f5', borderRadius: '4px' },
};

export default SystemMessagesTab;
